import React from "react";

import { MeterBarWidget } from "./MeterBarWidget";

/** One resolved meter spec — the already-derived fraction + labels + fill,
 *  ready to hand to a `MeterBarWidget`. */
export interface ProgramMeter {
    title: string;
    fraction: number;
    fillLabel: string;
    trailLabel: string;
    fillColor: string;
}

export interface ProgramProgressProps {
    /** Budget meter — present only for fixed-budget programs (`null` when the
     *  program has an open budget). */
    budgetMeter: ProgramMeter | null;
    /** Timeline meter — present for all fixed-length programs (`null` for
     *  continuous programs, which render nothing). */
    timelineMeter: ProgramMeter | null;
}

/**
 * ProgramProgress — **View** (props-only). Renders the active-program budget +
 * timeline progress meters — stacked, capped width — from already-resolved
 * meter specs. Sits directly under the `ProgramKeyStats` row in the read view.
 *
 * Pure render: no participant reads, no budget/timeline math. When both meters
 * are `null` (continuous programs) it renders nothing — no empty bordered band
 * under the key-stats row. All derivation lives in `ProgramProgressContainer`
 * (app-side).
 *
 * Hoisted into `shared/widgets/yaas/programs` so external consumers (the
 * "+New Program" wiki doc) can render the bare progress meters fixture-driven,
 * with no wallet / RPC / orgApiFetch. The app keeps a re-export shim at the old
 * path so `yaas-client` compiles unchanged.
 */
export function ProgramProgress({
    budgetMeter,
    timelineMeter
}: ProgramProgressProps): React.ReactElement {
    // Continuous programs pass both meters `null` — render nothing (no empty
    // bordered band under the key-stats row).
    if (!budgetMeter && !timelineMeter) {
        return <></>;
    }

    return (
        // The key-stats row above already carries the bottom rule; this meters
        // section only needs its own bottom rule (no top border → no 2px
        // stack). Section wrapper carries px so the divider runs edge-to-edge.
        <section className="px-4 py-6 border-b border-line-base">
            <div className="flex flex-col gap-6 max-w-lg">
                {budgetMeter && (
                    <MeterBarWidget
                        title={budgetMeter.title}
                        fraction={budgetMeter.fraction}
                        fillLabel={budgetMeter.fillLabel}
                        trailLabel={budgetMeter.trailLabel}
                        fillColor={budgetMeter.fillColor}
                    />
                )}
                {timelineMeter && (
                    <MeterBarWidget
                        title={timelineMeter.title}
                        fraction={timelineMeter.fraction}
                        fillLabel={timelineMeter.fillLabel}
                        trailLabel={timelineMeter.trailLabel}
                        fillColor={timelineMeter.fillColor}
                    />
                )}
            </div>
        </section>
    );
}
